/**
 * Navegación, con la empresa dentro de la dirección.
 *
 * Se usa el # en vez de una librería de rutas. Son cuarenta líneas contra 15 KB
 * que tendría que descargar el celular del mensajero en la calle, y la app
 * tiene quince pantallas, no cincuenta.
 *
 * **Por qué la empresa va en la dirección.** Una cuenta puede pertenecer a
 * varias empresas. Si cuál está mirando viviera en una variable que la
 * aplicación recuerda, bastaría con que esa variable quedara desactualizada
 * —una pestaña vieja, un botón de atrás, una pantalla que no se enteró— para
 * editar los datos de una empresa creyendo que son los de otra. Y las reglas de
 * Firestore no lo impedirían, porque el usuario pertenece legítimamente a las
 * dos: para la base de datos sería una operación válida.
 *
 * Poniéndola en la dirección, «en cuál estoy» deja de ser algo que el programa
 * recuerda y pasa a ser algo que se ve. No hay dos fuentes que puedan
 * discrepar, el enlace que alguien comparte lleva su empresa adentro, y el
 * botón de atrás hace lo obvio.
 *
 * La dirección real es `#/e/IGS/clientes`, pero las pantallas siguen hablando
 * de `/clientes`: el prefijo se pone y se quita aquí. Por eso las cincuenta
 * llamadas a `ir()` del resto de la aplicación no saben que esto existe.
 */

import { useEffect, useState } from 'react';

const PREFIJO = /^\/e\/([A-Za-z0-9_-]+)(\/.*)?$/;

function hashCrudo(): string {
  return window.location.hash.slice(1) || '/';
}

/** El código de empresa que va en la dirección, o `null` si no lleva. */
export function empresaDeRuta(hash = hashCrudo()): string | null {
  return PREFIJO.exec(hash)?.[1] ?? null;
}

/** La ruta sin el prefijo de empresa: lo que las pantallas entienden. */
export function rutaLogica(hash = hashCrudo()): string {
  const m = PREFIJO.exec(hash);
  if (!m) return hash;
  return m[2] || '/';
}

/**
 * La empresa que se está mirando ahora mismo.
 *
 * Lanza si no hay ninguna en la dirección, y eso es deliberado: una consulta a
 * la base sin empresa no tiene un valor por defecto razonable. Es mejor un
 * error ruidoso que leer los datos equivocados en silencio.
 */
export function empresaActiva(): string {
  const e = empresaDeRuta();
  if (!e) throw new Error('No hay ninguna empresa en la dirección.');
  return e;
}

/**
 * Navega.
 *
 * Una ruta que empieza por `/e/` se respeta tal cual —así se cambia de
 * empresa—; cualquier otra hereda la empresa de donde estás. Si no hay ninguna
 * todavía, se navega sin prefijo y el enrutador se encarga de mandar a la
 * pantalla de entrada.
 */
export function ir(r: string) {
  if (r.startsWith('/e/')) { window.location.hash = r; return; }
  const e = empresaDeRuta();
  window.location.hash = e ? `/e/${e}${r === '/' ? '' : r}` : r;
}

/**
 * La misma dirección que abriría `ir(r)`, pero como texto para un `href`.
 *
 * Un enlace de verdad deja que el navegador haga lo suyo: clic derecho,
 * «abrir en una pestaña nueva», el botón del medio. Un botón con `onClick`
 * no. Para las acciones de abrir una ficha, esto es lo que se usa.
 */
export function enlace(r: string): string {
  if (r.startsWith('/e/')) return `#${r}`;
  const e = empresaDeRuta();
  return `#${e ? `/e/${e}${r === '/' ? '' : r}` : r}`;
}

/*
 * La última empresa propia en la que se trabajó.
 *
 * Solo sirve para una cosa: cuando se abre la aplicación **sin** empresa en la
 * dirección —desde el ícono instalado, un favorito, tras una actualización— ir
 * a donde la persona estaba y no a la primera de su lista. No es una segunda
 * fuente de verdad: en cuanto la dirección lleva empresa, manda la dirección.
 */
const LLAVE_ULTIMA = 'guerrera.ultima_empresa';

export function recordarEmpresa(codigo: string): void {
  try { window.localStorage.setItem(LLAVE_ULTIMA, codigo); } catch { /* modo privado: no pasa nada */ }
}

export function empresaRecordada(): string | null {
  try { return window.localStorage.getItem(LLAVE_ULTIMA); } catch { return null; }
}

/** Cambia de empresa conservando la pantalla en la que estás. */
export function irAEmpresa(codigo: string) {
  const r = rutaLogica();
  window.location.hash = `/e/${codigo}${r === '/' ? '' : r}`;
}

export function useRuta(): [string, (r: string) => void] {
  const [hash, setHash] = useState(hashCrudo);

  useEffect(() => {
    const alCambiar = () => setHash(hashCrudo());
    window.addEventListener('hashchange', alCambiar);
    return () => window.removeEventListener('hashchange', alCambiar);
  }, []);

  return [rutaLogica(hash), ir];
}

/**
 * Los parámetros de la dirección: `/nuevo?prospecto=abc` -> `{prospecto:'abc'}`.
 *
 * Sirven para llevar un borrador de una pantalla a otra sin guardarlo en la
 * base. Un prospecto que se está convirtiendo todavía no es un cliente, y
 * escribirlo en la maestra antes de que alguien confirme el formulario dejaría
 * cuentas a medias que nadie puede usar.
 */
export function parametros(ruta: string): Record<string, string> {
  const i = ruta.indexOf('?');
  if (i < 0) return {};
  return Object.fromEntries(new URLSearchParams(ruta.slice(i + 1)));
}

/** El camino sin parámetros, que es contra lo que compara el enrutador. */
export function camino(ruta: string): string {
  const i = ruta.indexOf('?');
  return i < 0 ? ruta : ruta.slice(0, i);
}

/** La empresa de la dirección, en vivo. */
export function useEmpresaDeRuta(): string | null {
  const [hash, setHash] = useState(hashCrudo);
  useEffect(() => {
    const alCambiar = () => setHash(hashCrudo());
    window.addEventListener('hashchange', alCambiar);
    return () => window.removeEventListener('hashchange', alCambiar);
  }, []);
  return empresaDeRuta(hash);
}
